import React, { useEffect, useId, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaWhatsapp } from 'react-icons/fa';
import Pacientes from '../../services/Pacientes';
import { agendamentoWhatsappLink, nowText, upcomingSessions } from '../../utilities/agendamentoWhatsapp';
import { whatsappNumber } from '../../utilities/birthdays';
import { formatarTelefone } from '../../utilities/formatarTelefoneInput';

// Foco perdido: no body, no próprio contêiner do modal (para onde o modal leva o foco) ou fora do modal do link
function focusLost(link) {
  const dialog = link?.closest('.modal');
  const active = document.activeElement;
  return !active || active === document.body || active === dialog || !dialog?.contains(active);
}

// "Salvar e enviar": abre o WhatsApp do paciente numa aba nova logo depois de salvar, ainda dentro do clique (passados
// alguns segundos, o navegador bloqueia). Devolve 'opened', 'blocked' ou null (sem celular ou sem sessão futura: não abre).
// Sem 'noopener' nas opções, que faria o window.open devolver sempre null: o opener é desligado logo depois
export function openAgendamentoWhatsapp(patient, sessions) {
  const link = whatsappNumber(patient) ? agendamentoWhatsappLink(patient, sessions) : null;
  if (!link) return null;

  const page = window.open(link, '_blank');
  if (!page) return 'blocked';

  page.opener = null;
  return 'opened';
}

const OPENED_TEXT = {
  opened: { className: 'text-success', text: 'WhatsApp aberto em outra aba: confira a mensagem e envie por lá.' },
  blocked: { className: 'text-warning-emphasis', text: 'O navegador bloqueou a abertura do WhatsApp. Clique em "Enviar no WhatsApp".' },
};

// Envio ao paciente, pelo WhatsApp, da data e do horário dos agendamentos cadastrados (no resumo, depois de salvar).
// O celular vem da resposta da gravação. Sem ela (lote reconhecido depois de ficar sem resposta), lê o cadastro do paciente.
// opened: resultado da abertura automática do "Salvar e enviar" ('opened' ou 'blocked')
const AgendamentoWhatsapp = ({ patient, pacienteId, sessions, opened }) => {
  const [loaded, setLoaded] = useState(null); // { paciente } ou { failed: true }
  const linkRef = useRef(null);
  const statusId = useId();
  const needsLoad = !patient && Boolean(pacienteId);

  useEffect(() => {
    if (!needsLoad) return undefined;

    let active = true;
    setLoaded(null);
    Pacientes.listPessoaById(pacienteId)
      .then(({ data }) => { if (active) setLoaded({ paciente: data }); })
      .catch(() => { if (active) setLoaded({ failed: true }); });

    return () => { active = false; };
  }, [needsLoad, pacienteId]);

  // Bloqueado: o link fica em foco, e o Enter já abre o WhatsApp. Um modal que fechava por cima (a pergunta ou o modal
  // do "Lançar atendimento") puxa o foco para ele até sumir, e num computador lento isso demora: por 2 s, o foco volta
  // ao link sempre que estiver perdido, sem tirá-lo de quem já foi para outro botão do modal
  useEffect(() => {
    if (opened !== 'blocked') return undefined;

    linkRef.current?.focus();
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += 100;
      if (focusLost(linkRef.current)) linkRef.current?.focus();
      if (elapsed >= 2000) clearInterval(timer);
    }, 100);
    return () => clearInterval(timer);
  }, [opened]);

  const now = nowText();
  const upcoming = upcomingSessions(sessions, now);
  // Sessões válidas ('' como agora: todas)
  const validCount = upcomingSessions(sessions, '').length;

  // Tudo no passado (o horário passou antes do clique, ou lote com datas passadas): nada a enviar, e o resumo diz por quê
  if (upcoming.length === 0) {
    return validCount > 0
      ? <small className='d-block text-muted mt-2'>{validCount === 1 ? 'A data e o horário já passaram' : 'As datas e os horários já passaram'}: não há mensagem para enviar ao paciente.</small>
      : null;
  }

  if (needsLoad && !loaded) {
    return (
      <small className='d-flex align-items-center gap-2 text-muted mt-2'>
        <Spinner animation='border' size='sm' /> Buscando o celular do paciente...
      </small>
    );
  }

  const paciente = patient ?? loaded?.paciente ?? null;
  const number = whatsappNumber(paciente);
  const link = agendamentoWhatsappLink(paciente, sessions, now);
  const pastCount = validCount - upcoming.length;
  const note = number
    ? `Celular: ${formatarTelefone(number.slice(2))}`
    : (loaded?.failed ? 'Não foi possível ler o cadastro do paciente: escolha o contato no WhatsApp.' : 'Paciente sem celular no cadastro: escolha o contato no WhatsApp.');

  return (
    <div className='border rounded p-2 mt-2'>
      <div className='d-flex flex-wrap align-items-center gap-2'>
        <FaWhatsapp size={24} className='flex-shrink-0' style={{ color: '#25d366' }} aria-hidden='true' />
        <div className='d-flex flex-column flex-grow-1' style={{ minWidth: 0 }}>
          <span className='fw-semibold'>{upcoming.length === 1 ? 'Enviar a data e o horário ao paciente' : 'Enviar as datas e os horários ao paciente'}</span>
          <small className='text-muted'>{note}</small>
          {pastCount > 0 ? <small className='text-muted'>{pastCount === 1 ? 'A sessão que já passou fica fora da mensagem.' : 'As sessões que já passaram ficam fora da mensagem.'}</small> : ''}
        </div>
        {/* Link, e não botão: abre outra aba (o Enter abre; o aviso de bloqueio fica ligado a ele) */}
        <Button
          ref={linkRef}
          role='link'
          variant='outline-success'
          size='sm'
          className='d-flex align-items-center justify-content-center gap-1 text-nowrap'
          href={link}
          target='_blank'
          rel='noopener noreferrer'
          title='Abre o WhatsApp com a mensagem pronta. Confira e envie por lá.'
          aria-describedby={OPENED_TEXT[opened] ? statusId : undefined}
        >
          <FaWhatsapp aria-hidden='true' /> {number ? 'Enviar no WhatsApp' : 'Escolher contato no WhatsApp'}
        </Button>
      </div>
      {OPENED_TEXT[opened] ? <small id={statusId} role='status' className={`d-block mt-2 ${OPENED_TEXT[opened].className}`}>{OPENED_TEXT[opened].text}</small> : ''}
    </div>
  );
};

export default AgendamentoWhatsapp;
